import React from "react";
import { Button, Dropdown, Icon, Image, Input } from "semantic-ui-react";
import { PAYMENT_METHODS, getPaymentMethod } from "../../../../utils/constants";

const METHOD_OPTIONS = PAYMENT_METHODS.map((method) => ({
  key: method.value,
  value: method.value,
  text: method.text,
  icon: method.icon,
}));
const MAX_PARTS = 4;
const isWallet = (method) => method === "YAPE" || method === "PLIN";
const round2 = (value) => Math.round(value * 100) / 100;

export function partsAssigned(parts) {
  return round2(parts.reduce((sum, part) => sum + (Number(part.amount) || 0), 0));
}

export function cashPartAmount(parts) {
  return round2(
    parts.filter((part) => part.method === "CASH").reduce((sum, part) => sum + (Number(part.amount) || 0), 0)
  );
}

// Partes con monto: una fila en S/ 0 (o vacía) no cuenta — si al final todo
// va en una sola forma (ej. Efectivo 64, Yape 0), se cobra como pago simple.
export function activeParts(parts) {
  return parts.filter((part) => Number(part.amount) > 0);
}

// El cajero escribe en la fila de efectivo lo que el cliente LE ENTREGA (pasó
// dos veces probándolo: S/ 65 en una cuenta de 64, S/ 30 en una de 25). Si en
// efectivo se escribió más de lo que falta, el exceso es el vuelto: lo cobrado
// en efectivo baja hasta cuadrar y lo escrito queda como "efectivo entregado".
// Solo el efectivo da vuelto: con tarjeta/Yape/Plin no puede sobrar.
export function settleParts(parts, total) {
  const active = activeParts(parts).map((part) => ({ ...part }));
  const cashGiven = cashPartAmount(active);
  let excess = round2(partsAssigned(active) - total);
  let change = 0;
  if (excess > 0 && cashGiven >= excess) {
    change = excess;
    for (let i = active.length - 1; i >= 0 && excess > 0; i--) {
      if (active[i].method !== "CASH") continue;
      const amount = Number(active[i].amount);
      const cut = Math.min(amount, excess);
      active[i].amount = round2(amount - cut);
      excess = round2(excess - cut);
    }
  }
  return { parts: activeParts(active), cashGiven, change };
}

// Por qué todavía no se puede cobrar (null = listo). Mismo criterio que el
// backend: cuadra al céntimo y Yape/Plin con su código de operación.
export function partsProblem(parts, total) {
  const settled = settleParts(parts, total).parts;
  const remaining = round2(total - partsAssigned(settled));
  if (settled.length === 0 || remaining > 0) {
    return `Falta asignar S/ ${remaining.toFixed(2)} en el reparto`;
  }
  if (remaining < 0) return `Sobra S/ ${Math.abs(remaining).toFixed(2)} en el reparto`;
  const noCode = settled.find((part) => isWallet(part.method) && !part.operation_code.trim());
  if (noCode) return `Falta el código de operación de ${getPaymentMethod(noCode.method).text}`;
  return null;
}

// Pago mixto: parte en una forma y parte en otra (ej. S/ 20 en efectivo y el
// resto con Yape). Cada fila es una forma de pago con su monto; "Resto" pone
// en esa fila lo que falta para completar la cuenta.
export function MixedPayment(props) {
  const { total, parts, onChange, siteConfig } = props;
  const settled = settleParts(parts, total);
  const remaining = round2(total - partsAssigned(settled.parts));

  const update = (index, patch) =>
    onChange(parts.map((part, i) => (i === index ? { ...part, ...patch } : part)));
  const remove = (index) => onChange(parts.filter((_, i) => i !== index));
  const add = () => {
    const used = parts.map((part) => part.method);
    const next = PAYMENT_METHODS.find((method) => !used.includes(method.value))?.value || "CASH";
    onChange([...parts, { method: next, amount: remaining > 0 ? remaining.toFixed(2) : "", operation_code: "" }]);
  };

  const wallets = [...new Set(activeParts(parts).map((part) => part.method).filter(isWallet))];

  return (
    <div className="payment-detail__mixed">
      <h4>Pago mixto</h4>
      {parts.map((part, index) => (
        <div key={index} className="payment-detail__mixed-row">
          <Dropdown
            selection
            compact
            options={METHOD_OPTIONS}
            value={part.method}
            onChange={(_, data) => update(index, { method: data.value })}
          />
          <Input
            labelPosition="left"
            type="number"
            min="0"
            step="0.10"
            placeholder="0.00"
            value={part.amount}
            onChange={(_, data) => update(index, { amount: data.value })}
            label={{ basic: true, content: "S/" }}
            className="payment-detail__mixed-amount"
          />
          {remaining > 0 && (
            <Button
              basic
              size="small"
              type="button"
              onClick={() =>
                update(index, { amount: round2((Number(part.amount) || 0) + remaining).toFixed(2) })
              }
              title="Pone en esta forma de pago lo que falta para completar la cuenta"
            >
              Resto
            </Button>
          )}
          {parts.length > 2 && (
            <Button basic icon size="small" type="button" onClick={() => remove(index)} title="Quitar">
              <Icon name="close" />
            </Button>
          )}
          {isWallet(part.method) && (
            <Input
              fluid
              placeholder={`Código de operación ${getPaymentMethod(part.method).text}`}
              autoFocus
              value={part.operation_code}
              onChange={(_, data) => update(index, { operation_code: data.value })}
              className="payment-detail__mixed-code"
            />
          )}
        </div>
      ))}

      {parts.length < MAX_PARTS && (
        <Button basic size="small" type="button" onClick={add}>
          <Icon name="plus" /> Agregar otra forma de pago
        </Button>
      )}

      <div
        className={`payment-detail__mixed-status${
          remaining === 0 ? " payment-detail__mixed-status--ok" : ""
        }`}
      >
        {remaining === 0
          ? `✓ Cuadra: S/ ${round2(total).toFixed(2)}${
              settled.change > 0
                ? ` · vuelto S/ ${settled.change.toFixed(2)} del efectivo`
                : ""
            }`
          : remaining > 0
          ? `Falta asignar S/ ${remaining.toFixed(2)}`
          : `Sobra S/ ${Math.abs(remaining).toFixed(2)} — con tarjeta, Yape o Plin no hay vuelto: revisa los montos`}
      </div>

      {wallets.map((wallet) => {
        const qr = wallet === "YAPE" ? siteConfig?.yape_qr : siteConfig?.plin_qr;
        const text = getPaymentMethod(wallet).text;
        return qr ? (
          <div key={wallet} className="payment-detail__mixed-qr">
            <span>QR de {text}</span>
            <Image src={qr} centered />
          </div>
        ) : (
          <p key={wallet} className="payment-detail__no-qr">
            Aún no se subió el QR de {text}. Súbelo en Configuración → Pagos (Yape/Plin).
          </p>
        );
      })}
    </div>
  );
}
